import type { IngredientId } from '@libs/trip-planner-interfaces';
import { CSSCustomProperty } from '@libs/ui';

export interface LatLongPoint {
  latitude: number;
  longitude: number;
}

export type PriceMarkerType =
  | 'normal'
  | 'inspected'
  | 'selected'
  | 'selected-inspected'
  | 'included'
  | 'included-inspected'
  | 'previously-seen';

export interface PriceMarker {
  ingredientId: IngredientId;
  productCode: string; // NOTE: hotelCode / activityCode
  point: LatLongPoint;
  label: string;
  state: PriceMarkerType;
}

export interface ProductSelectionMapData {
  markers: PriceMarker[];
  // NOTE: Position of currently hovered product on list
  highlightMarker: LatLongPoint | undefined;
  // NOTE: Contains suggested hotel
  includedMarkers: PriceMarker[];
  // NOTE: Contains selected hotel / activities
  selectedMarkers: PriceMarker[];
}

// NOTE: Fields reflect Mabox API
export interface PriceMarkersSettings {
  clusterRadius: number;
  clusterMinPoints: number;
  iconAllowOverlap: boolean;
  iconIgnorePlacement: boolean;
  textAllowOverlap: boolean;
  textIgnorePlacement: boolean;
}

// NOTE:
// Previously tested
// clusterRadius: 40, 50 and clusterMinPoints: 3
// Both performed worse.
export const PRICE_MARKERS_SETTINGS: PriceMarkersSettings = {
  clusterRadius: 30,
  clusterMinPoints: 3,
  iconAllowOverlap: true,
  iconIgnorePlacement: true,
  textAllowOverlap: false,
  textIgnorePlacement: false,
};

export type MapboxStyleType = 'streets' | 'satellite';

export const MAPBOX_STYLE: Record<MapboxStyleType, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
};

export interface PriceMarkerStyles {
  fill: string;
  stroke: string;
}

export const PRICE_MARKER_STYLES: Record<PriceMarkerType, PriceMarkerStyles> = {
  normal: {
    fill: CSSCustomProperty.colorWhite,
    stroke: CSSCustomProperty.colorNeutral30,
  },
  included: {
    fill: CSSCustomProperty.colorBrandViolet,
    stroke: CSSCustomProperty.colorNeutral30,
  },
  selected: {
    fill: CSSCustomProperty.colorNeutral70,
    stroke: CSSCustomProperty.colorNeutral30,
  },
  inspected: {
    fill: CSSCustomProperty.colorBrandPop,
    stroke: CSSCustomProperty.colorBrandPop,
  },
  'selected-inspected': {
    fill: CSSCustomProperty.colorBrandPop,
    stroke: CSSCustomProperty.colorNeutral30,
  },
  'included-inspected': {
    fill: CSSCustomProperty.colorBrandPop,
    stroke: CSSCustomProperty.colorNeutral30,
  },
  'previously-seen': {
    fill: CSSCustomProperty.colorOffWhite,
    stroke: CSSCustomProperty.colorNeutral30,
  },
};

export interface PriceMarkerSize {
  width: number;
  name: string;
}

export const MARKER_WITH_CHECKBOX_SIZES: PriceMarkerSize[] = [
  { width: 60, name: 's' },
  { width: 68, name: 'm' },
  { width: 76, name: 'l' },
];

export const MARKER_WITHOUT_CHECKBOX_SIZES: PriceMarkerSize[] = [
  { width: 66, name: 's' },
  { width: 76, name: 'm' },
  { width: 86, name: 'l' },
];
